/**
 * useCanvasInterviewBridge — Bidirectional sync between canvas and interview agent.
 *
 * Canvas → Agent: Queues events (confirm, dismiss, context, cluster) that get
 * flushed and sent with the next chat API call.
 *
 * Agent → Canvas: Processes commands (focus, highlight, cluster proposals) from
 * chat responses and delegates to navigation/clustering handlers.
 *
 * Highlight state: Tracks which nodes are currently highlighted with auto-clear
 * after 5 seconds of no re-highlight.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CanvasCommand,
  CanvasContextSummary,
  CanvasEvent,
  CanvasEventType,
  CanvasCluster,
  CanvasNodeSummary,
  CanvasAnnotationSummary,
  DismissedNodeSummary,
  CanvasClusterSummary,
} from '@shared/types.ts';
import type { CanvasFlowNode } from './useProcessCanvas';
import type { ClusterProposal } from './useClusterManager';

const HIGHLIGHT_DURATION_MS = 5000;

/** Callbacks for handling agent→canvas commands */
export interface BridgeCommandHandlers {
  focusOnNode: (nodeId: string) => void;
  focusOnArea: (areaId: string) => void;
  proposeClusters: (proposals: ClusterProposal[]) => void;
}

export interface UseCanvasInterviewBridgeReturn {
  /** Queue a canvas event to be sent with the next chat message */
  enqueueEvent: (type: CanvasEventType, payload: CanvasEvent['payload']) => void;
  /** Drain and return all queued events (call before API request) */
  flushEvents: () => CanvasEvent[];
  /** Build a canvas context summary from current state */
  buildCanvasContext: (nodes: CanvasFlowNode[], clusters: CanvasCluster[]) => CanvasContextSummary;
  /** Process agent commands from a chat response */
  handleCommands: (
    commands: CanvasCommand[] | undefined,
    handlers: BridgeCommandHandlers,
  ) => void;
  /** Set of currently highlighted node IDs (for rendering) */
  highlightedNodeIds: Set<string>;
}

let eventCounter = 0;

export function useCanvasInterviewBridge(
  persistedDismissedNodes?: Array<{ id: string; name: string; type: string; dismissedAt: string }>,
): UseCanvasInterviewBridgeReturn {
  // Event queue — ref to avoid re-renders on enqueue
  const eventQueueRef = useRef<CanvasEvent[]>([]);
  // Recent changes log for context building (human-readable strings)
  const recentChangesRef = useRef<string[]>([]);
  // Track dismissed nodes so the coordinator knows not to ask about them
  const dismissedNodesRef = useRef<DismissedNodeSummary[]>(
    persistedDismissedNodes?.map((d) => ({
      id: d.id,
      name: d.name,
      type: d.type,
      dismissedAt: d.dismissedAt,
    })) ?? [],
  );

  // Highlight state
  const [highlightedNodeIds, setHighlightedNodeIds] = useState<Set<string>>(new Set());
  const highlightTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup highlight timers on unmount
  useEffect(() => {
    return () => {
      for (const timer of highlightTimersRef.current.values()) {
        clearTimeout(timer);
      }
    };
  }, []);

  const enqueueEvent = useCallback(
    (type: CanvasEventType, payload: CanvasEvent['payload']) => {
      const event: CanvasEvent = {
        id: `ce-${Date.now()}-${++eventCounter}`,
        timestamp: new Date().toISOString(),
        type,
        payload,
      };
      eventQueueRef.current.push(event);

      // Track as recent change for context
      const label = payload.nodeLabel ?? payload.clusterName ?? payload.nodeId ?? 'unknown';
      const nodeType = payload.nodeType ?? '';
      switch (type) {
        case 'nodeConfirmed':
          recentChangesRef.current.push(`User confirmed "${label}" (${nodeType})`);
          break;
        case 'nodeDismissed':
          recentChangesRef.current.push(`User dismissed "${label}" (${nodeType})`);
          // Track for coordinator awareness — these nodes should never be re-asked
          dismissedNodesRef.current.push({
            id: payload.nodeId ?? '',
            name: label,
            type: nodeType,
            dismissedAt: new Date().toISOString(),
          });
          break;
        case 'nodeContextAdded':
          recentChangesRef.current.push(`User added context to "${label}"`);
          break;
        case 'clusterApproved':
          recentChangesRef.current.push(`User approved cluster "${label}"`);
          break;
        case 'clusterDismissed':
          recentChangesRef.current.push(`User dismissed cluster proposal`);
          break;
      }
      // Keep only last 10 changes
      if (recentChangesRef.current.length > 10) {
        recentChangesRef.current = recentChangesRef.current.slice(-10);
      }
    },
    [],
  );

  const flushEvents = useCallback((): CanvasEvent[] => {
    const events = [...eventQueueRef.current];
    eventQueueRef.current = [];
    return events;
  }, []);

  const buildCanvasContext = useCallback(
    (nodes: CanvasFlowNode[], clusters: CanvasCluster[]): CanvasContextSummary => {
      let confirmedCount = 0;
      let stagedCount = 0;
      const pendingStagedNodes: CanvasContextSummary['pendingStagedNodes'] = [];
      const confirmedNodes: CanvasNodeSummary[] = [];
      const annotations: CanvasAnnotationSummary[] = [];

      for (const node of nodes) {
        // Skip section group nodes
        if (node.type === 'sectionGroup') continue;

        if (node.data.staged) {
          stagedCount++;
          pendingStagedNodes.push({
            id: node.id,
            name: node.data.label,
            type: node.data.canvasNodeType,
          });
        } else {
          confirmedCount++;
          // Skip preloaded "existing" nodes — coordinator already knows about prior knowledge
          if (node.data.sourceType !== 'existing') {
            const hasAnnotations = (node.data.additionalContext?.length ?? 0) > 0;
            confirmedNodes.push({
              id: node.id,
              type: node.data.canvasNodeType,
              name: node.data.label,
              description: node.data.description?.slice(0, 100),
              section: node.data.section,
              hasAnnotations,
              annotationSnippet: hasAnnotations
                ? node.data.additionalContext![0].text.slice(0, 150)
                : undefined,
            });

            // Collect all annotations
            if (node.data.additionalContext) {
              for (const ctx of node.data.additionalContext) {
                annotations.push({
                  nodeId: node.id,
                  nodeName: node.data.label,
                  text: ctx.text.slice(0, 150),
                  addedAt: ctx.timestamp,
                });
              }
            }
          }
        }
      }

      // Build cluster summaries
      const clusterSummaries: CanvasClusterSummary[] = clusters.map((c) => {
        const clusterNodes = nodes.filter((n) => c.nodeIds.includes(n.id));
        return {
          name: c.name,
          nodeCount: c.nodeIds.length,
          nodeTypes: clusterNodes.map((n) => n.data.canvasNodeType),
          nodeIds: c.nodeIds,
        };
      });

      return {
        nodeCount: confirmedCount + stagedCount,
        confirmedCount,
        stagedCount,
        clusterCount: clusters.length,
        recentChanges: [...recentChangesRef.current],
        pendingStagedNodes,
        confirmedNodes: confirmedNodes.length > 0 ? confirmedNodes : undefined,
        dismissedNodes: dismissedNodesRef.current.length > 0
          ? [...dismissedNodesRef.current]
          : undefined,
        annotations: annotations.length > 0 ? annotations : undefined,
        clusters: clusterSummaries.length > 0 ? clusterSummaries : undefined,
      };
    },
    [],
  );

  const addHighlight = useCallback((nodeId: string) => {
    // Clear existing timer for this node if re-highlighted
    const existingTimer = highlightTimersRef.current.get(nodeId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    setHighlightedNodeIds((prev) => {
      const next = new Set(prev);
      next.add(nodeId);
      return next;
    });

    // Auto-clear after duration
    const timer = setTimeout(() => {
      setHighlightedNodeIds((prev) => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
      highlightTimersRef.current.delete(nodeId);
    }, HIGHLIGHT_DURATION_MS);

    highlightTimersRef.current.set(nodeId, timer);
  }, []);

  const clearAllHighlights = useCallback(() => {
    for (const timer of highlightTimersRef.current.values()) {
      clearTimeout(timer);
    }
    highlightTimersRef.current.clear();
    setHighlightedNodeIds(new Set());
  }, []);

  const handleCommands = useCallback(
    (
      commands: CanvasCommand[] | undefined,
      handlers: BridgeCommandHandlers,
    ) => {
      if (!commands || commands.length === 0) return;

      for (const command of commands) {
        switch (command.type) {
          case 'focusNode':
            if (command.payload.nodeId) {
              handlers.focusOnNode(command.payload.nodeId);
            }
            break;
          case 'focusArea':
            if (command.payload.areaId) {
              handlers.focusOnArea(command.payload.areaId);
            }
            break;
          case 'highlightNode':
            if (command.payload.nodeId) {
              addHighlight(command.payload.nodeId);
            }
            break;
          case 'proposeClustering':
            if (command.payload.clusters) {
              handlers.proposeClusters(
                command.payload.clusters.map((c) => ({
                  id: c.id,
                  name: c.name,
                  nodeIds: c.nodeIds,
                })),
              );
            }
            break;
          case 'clearHighlights':
            clearAllHighlights();
            break;
        }
      }
    },
    [addHighlight, clearAllHighlights],
  );

  return {
    enqueueEvent,
    flushEvents,
    buildCanvasContext,
    handleCommands,
    highlightedNodeIds,
  };
}
